<template>
  <div class="ai-help-me-write-generated-content-wrapper">
    <div class="text-and-btn d-flex flex-column flex-md-row align-center">
      <span class="text font-outfit-medium">{{ $t('aiGeneratedContent') }}</span>
      <ib-button-basic
        v-if="!inputContent"
        height="36px"
        :disabled="loading || generating"
        @click="autoWrite"
      >
        <span class="font-outfit-medium">{{ $t('autoWrite') }}</span>
      </ib-button-basic>
      <ai-help-me-write-dropdown
        v-if="inputContent"
        v-model="selectedMethod"
        :disabled="loading || generating"
        @change="onChange"
      />
    </div>

    <Transition name="slide">
      <div
        v-show="show"
        class="generate-content"
      >
        <IbBlinkingAnimationSentences v-if="loading" class="ib-blinking-animation-sentences" :sentences="[
          $t('aiBuddy.getSuggestions.sentences.sentence01'),
          $t('aiBuddy.getSuggestions.sentences.sentence02'),
          $t('aiBuddy.getSuggestions.sentences.sentence03'),
          $t('aiBuddy.getSuggestions.sentences.sentence04'),
          $t('aiBuddy.getSuggestions.sentences.sentence05'),
          $t('aiBuddy.getSuggestions.sentences.sentence06'),
          $t('aiBuddy.getSuggestions.sentences.sentence07'),
          $t('aiBuddy.getSuggestions.sentences.sentence08')
        ]"
        />
        <vue-typed-js
          v-if="responseText && !stopGenerating"
          ref="typedRef"
          :strings="[responseText]"
          :content-type="'html'"
          :shuffle="false"
          :start-delay="10"
          :type-speed="1"
          :show-cursor="false"
          @onStart="generating = true"
          @onComplete="generating = false"
        >
          <p class="typing ma-0" />
        </vue-typed-js>

        <p
          v-if="responseText && stopGenerating"
          class="typing ma-0"
          v-html="responseText"
        />

        <ib-divider v-if="!loading" block class="mb-3 mt-0" />

        <p v-if="!responseText && !loading" class="generated-content-will-appear-here">
          {{ $t('generatedContentWillAppearHere') }}
        </p>

        <div class="d-flex justify-center justify-md-end">
          <div v-if="generating" class="stop-generating-button-wrapper">
            <ib-button-basic
              icon="ib-icon-stop"
              @click="stop"
            >
              {{ $t('stop') }}
            </ib-button-basic>
          </div>

          <template v-if="!generating && responseText">
            <div class="d-flex flex-column flex-md-row generating-loading-buttons-wrapper">
              <ib-button-basic
                filled
                icon="ib-icon-swap"
                @click="replaceContent"
              >
                {{ inputContent ? $t('replaceContent') : $t('insertContent') }}
              </ib-button-basic>
              <ib-button-basic
                icon="ib-icon-refresh"
                @click="callApi(selectedMethod)"
              >
                {{ $t('regenerate') }}
              </ib-button-basic>
            </div>
          </template>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import AiHelpMeWriteDropdown from '@/views/Home/StoryMode/Components/AiBuddy/AiHelpMeWriteDropdown.vue'
import IbBlinkingAnimationSentences from '@/components/_v2/IbBlinkingAnimation/IbBlinkingAnimationSentences.vue'
import aiBuddySections from '@/data/aiBuddySections'

import { mapActions, mapGetters } from 'vuex'
import MixinHandwriting from '@/mixins/handwriting'

export default {
  name: 'AiHelpMeWriteGeneratedContent',

  components: { IbBlinkingAnimationSentences, AiHelpMeWriteDropdown },

  mixins: [
    MixinHandwriting
  ],

  inject: ['content'],

  props: {
    type: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      show: true,
      responseText: null,
      loading: false,
      generating: false,
      selectedMethod: null,
      stopGenerating: false
    }
  },

  computed: {
    ...mapGetters('user', ['getAiRequests']),

    inputContent () {
      return this.content()
    }
  },

  methods: {
    ...mapActions('user', ['fetchAiRequests']),

    onChange (event) {
      this.handwritingSet('aiHelpMeWrite')
      this.selectedMethod = event
      this.callApi(event)
    },

    autoWrite () {
      this.handwritingSet('aiHelpMeWrite')
      this.selectedMethod = 'help-me-write'
      this.callApi('help-me-write')
    },

    async callApi (type) {
      try {
        this.stopGenerating = false
        this.show = true
        this.loading = true

        // setTimeout(() => {
        this.responseText = null
        // }, 350)

        const response = await this.$http.post(
            `ai/${type}/ai-buddy/${this.$store.state.idea.id}`,
            {
              sectionName: aiBuddySections[this.type],
              userInput: this.responseText ? this.responseText : this.inputContent,
              template: false
            }
        )

        setTimeout(() => {
          this.stop()
        }, 5000)

        this.loading = false
        this.generating = true

        this.responseText = response.data.payload.response
        await this.fetchAiRequests()

        const { amount, amountSpent } = this.getAiRequests

        this.$intercom.update({
          AICredit: amount,
          AIUsed: amountSpent
        })

        this.show = true
      } catch (e) {
        this.loading = false
      }
    },

    replaceContent () {
      this.$emit('replaceContent', this.responseText)
      this.selectedMethod = null
      this.responseText = null
    },

    stop () {
      this.stopGenerating = true
      this.generating = false
      this.$refs.typedRef.typedObj.pause.status = true
    }
  }
}
</script>

<style scoped lang="scss">
.ai-help-me-write-generated-content-wrapper {
  border-radius: 8px;
  border: 1px solid #C4C4C4;
  margin-bottom: 10px;

  .text-and-btn {
    background: rgba(133, 9, 246, 0.10);
    padding: 10px 0;
    row-gap: 10px;
    border-top-right-radius: 8px !important;
    border-top-left-radius: 8px !important;

    @include media-breakpoint-up($md) {
      column-gap: 10px;
      padding: 10px 0 10px 15px;
    }

    .text {
      color: $color-ai;
    }
  }

  .ib-blinking-animation-sentences {
    padding: 16px 30px;
  }

  .generate-content {
    border-radius: 0 0 6px 6px;
    background-color: #FAFBFF;
    min-height: 190px;
    overflow: hidden;
    border-bottom-right-radius: 8px !important;
    border-bottom-left-radius: 8px !important;

    .generated-content-will-appear-here {
      color: $color-navy;
      font-size: 16px;
      font-family: $outfit-light;
      opacity: .6;
      padding-left: 30px;
    }

    .typing {
      max-height: 54vh;
      overflow: auto;
      padding: 16px 30px;

      ::v-deep {

        h1,h2,h3 {
          font-family: $outfit-light;
          margin: 24px 0 16px;
        }

        h1 {
          font-size: 24px !important;
        }
        h2 {
          font-size: 20px !important;
        }
        h3 {
          font-size: 18px !important;
        }

        strong {
          font-size: 18px;
          font-family: $outfit-light;
          margin: 24px 0 16px;
        }

        p {
          strong {
            font-size: 16px;
            margin: 24px 0 16px;
          }
        }
        ul, ol {
          strong {
            font-size: 16px;
            font-family: $outfit-light !important;
            margin: unset;
          }
        }
        img {
          width: 100%;
        }
      }
    }
  }

  .stop-generating-button-wrapper,
  .generating-loading-buttons-wrapper {
    padding: 0 15px 15px;
  }

  .generating-loading-buttons-wrapper {
    row-gap: 10px;

    @include media-breakpoint-up($md) {
      column-gap: 10px;
    }
  }
}
</style>
